import { useMemo } from 'react';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { Box, Typography } from '@mui/material';
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import PointhomeRetentionTable from './PointhomeRetentionTable';
import {
  PointhomeFunnelRetentionData,
} from '../../../../types/pointhomeFunnel.types';

type Props = {
  data: PointhomeFunnelRetentionData;
  selectedApp?: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
};

export default function PointhomeRetentionDashboard({ data, selectedApp, startDate, endDate }: Props) {
  const { chartData } = useMemo(() => {
    if (!data || data.length === 0) {
      return { chartData: [] };
    }

    // 데이터가 이미 정렬되어 있다고 가정하지만, 안전하게 날짜순 정렬 후 첫 번째 객체 사용
    const sorted = [...data].sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
    const baseRow = sorted[0];

    const chartData = (baseRow.retention ?? [])
      .slice()
      .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)))
      .map((entry) => {
        const labelDate = dayjs(entry.date);
        const newUserCount = baseRow.newUserCount;
        const actualRetained = entry.count ?? 0;
        
        // 잔존 수가 신규 유저 수보다 클 경우(데이터 오류 등) 100%를 넘지 않도록 제한
        const retainedCount = actualRetained > newUserCount ? newUserCount : actualRetained;
        const rate = newUserCount > 0 ? (retainedCount / newUserCount) * 100 : 0;

        return {
          label: labelDate.format('MM/DD'),
          retained: retainedCount,
          rate,
        };
      });

    return {
      chartData,
    };
  }, [data]);

  if (!data || data.length === 0) return null;

  return (
    <>
    <Box
      sx={{
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        width: '100%',
        maxWidth: '100%',
        borderRadius: 3,
        p: 2,
        mt: 3,
      }}
    >
      <Typography variant="h6" align="center" fontWeight="bold" sx={{ mb: 1 }}>
        잔존율 추이
      </Typography>
      <Box sx={{ width: '100%', height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 20, right: 24, left: 4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fill: '#6b7280' }} axisLine={{ stroke: '#e5e7eb' }} />
            <YAxis
              domain={[0, 'auto']}
              tickFormatter={(value) => `${value}%`}
              tick={{ fill: '#6b7280' }}
              axisLine={{ stroke: '#e5e7eb' }}
            />
            <RechartsTooltip
              formatter={(value: number) => [`${value.toFixed(1)}%`, '잔존율 (%)']}
              labelFormatter={(label) => `${label}`}
            />
            <Line
              type="monotone"
              dataKey="rate"
              stroke="#0044D2"
              strokeWidth={3}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
    <Box
      sx={{
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        width: '100%',
        maxWidth: '100%',
        borderRadius: 3,
        p: 2,
        mt: 3,
      }}
    >
      <PointhomeRetentionTable 
        data={data} 
        selectedApp={selectedApp}
        startDate={startDate}
        endDate={endDate}
      />
    </Box>
    </>
  );
}
